import { useState } from 'react';
import OtpInput from 'react-otp-input';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Button, message } from 'antd';
import { asyncWrap } from '../utils/utils';
import { useAuth } from '../context/auth-context';

function CounterBoy() {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpKey, setOtpKey] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const { user } = useAuth();
  const userRole = user?.profile?.roleName || user?.roleName;
  const isElectrician = userRole === 'Electrician';

  // ========================
  // 📩 STEP 1: SEND OTP
  // ========================
  const handleSendOtp = async () => {
    if (!name.trim()) {
      Swal.fire('Please enter your name');
      return;
    }

    if (!mobile || mobile.length !== 10) {
      Swal.fire('Please enter a valid 10-digit mobile number');
      return;
    }

    setSendingOtp(true);
    try {
      const [err, res] = await asyncWrap(
        axios.post('/counter-boy/sendOtp', { mobileNumber: mobile })
      );

      if (err || !res?.data?.success) {
        message.error(res?.data?.message || 'Failed to send OTP');
        return;
      }

      setOtpKey(res.data.otpKey);
      setOtpSent(true);
      message.success('OTP sent successfully!');
    } catch (error) {
      console.error('Send OTP error:', error);
      Swal.fire('Something went wrong while sending OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // ========================
  // ✅ STEP 2: VERIFY OTP
  // ========================
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      Swal.fire('Please enter the 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);

    try {
      const [err, res] = await asyncWrap(
        axios.post('/counter-boy/verifyOtp', {
          otpKey,
          otp,
          name,
          mobileNumber: mobile,
        })
      );

      if (err || !res?.data?.success) {
        message.error(res?.data?.message || 'OTP verification failed');
        return;
      }

      Swal.fire(res.data.message).then(() => {
        setSuccessMsg('🎉 Counter boy added successfully!');
      })
      // message.success('Counter boy added successfully!');
    } catch (error) {
      console.error('Verify OTP error:', error);
      Swal.fire('Something went wrong while verifying OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // ========================
  // 🎉 SUCCESS UI
  // ========================
  if (successMsg) {
    return (
      <div
        style={{
          padding: '40px',
          margin: '60px auto',
          maxWidth: '400px',
          background: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
          textAlign: 'center',
        }}
      >
        <div
          style={{ fontSize: '48px', color: '#13c2c2', marginBottom: '16px' }}
        >
          🎉
        </div>
        <div
          style={{
            fontWeight: 'bold',
            fontSize: '22px',
            color: '#13c2c2',
            marginBottom: '8px',
          }}
        >
          Success!
        </div>
        <div style={{ color: '#595959', fontSize: '16px' }}>{successMsg}</div>
      </div>
    );
  }

  // ========================
  // 🧾 MAIN UI
  // ========================
  if (isElectrician) {
    return (
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          padding: '40px 20px',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '32px 24px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1677ff',
              marginBottom: '12px',
            }}
          >
            Dealer Only Option
          </div>
          <div style={{ color: '#595959', fontSize: '16px' }}>
            This page is available only for dealer users.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className='d-flex flex-column align-items-center justify-content-center'
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        padding: '40px 20px',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '32px 24px',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#1677ff',
            marginBottom: '24px',
          }}
        >
          Counter Boy Registration
        </div>

        {/* Name Input */}
        <div className='input-group mb-3'>
          <span
            className='input-group-text'
            style={{ background: '#f0f4fa', fontWeight: 600 }}
          >
            Name
          </span>
          <input
            type='text'
            className='form-control p-2'
            placeholder='Enter your name'
            value={name}
            onChange={e => setName(e.target.value)}
            style={{
              borderRadius: '0 6px 6px 0',
              border: '1px solid #d9d9d9',
              fontSize: '16px',
            }}
          />
        </div>

        {/* Mobile Input */}
        <div className='input-group mb-3'>
          <span
            className='input-group-text'
            style={{ background: '#f0f4fa', fontWeight: 600 }}
          >
            +91
          </span>
          <input
            type='tel'
            className='form-control p-2'
            placeholder='Enter your phone number'
            value={mobile}
            onChange={e => setMobile(e.target.value)}
            style={{
              borderRadius: '0 6px 6px 0',
              border: '1px solid #d9d9d9',
              fontSize: '16px',
            }}
          />
        </div>

        {/* Show OTP input only after sending */}
        {otpSent && (
          <div className='mt-3 justify-content-center'>
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={6}
              isInputNum
              inputStyle={{
                width: '40px',
                height: '40px',
                textAlign: 'center',
                background: '#f5f7fa',
                margin: '0 6px',
                border: '1.5px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '18px',
                color: '#1677ff',
                fontWeight: 'bold',
              }}
              containerStyle={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
              focusStyle={{
                border: '2px solid #1677ff',
                background: '#e6f4ff',
              }}
            />
          </div>
        )}

        {/* Action Button */}
        {!otpSent ? (
          <Button
            type='primary'
            onClick={handleSendOtp}
            loading={sendingOtp}
            style={{
              width: '100%',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '16px',
              marginTop: '8px',
            }}
          >
            {sendingOtp ? 'Sending OTP...' : 'Send OTP'}
          </Button>
        ) : (
          <Button
            type='primary'
            onClick={handleVerifyOtp}
            loading={verifyingOtp}
            style={{
              width: '100%',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '16px',
              marginTop: '8px',
            }}
          >
            {verifyingOtp ? 'Verifying...' : 'Verify OTP & Save'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default CounterBoy;
